import {ReactNode, useMemo, useCallback} from 'react';
import {View, StyleSheet, FlatList, ViewStyle, RefreshControl, DimensionValue, useWindowDimensions} from 'react-native';

interface ResponsiveGridProps<T> {
    data: T[];
    renderItem: (item: T, index: number) => ReactNode;
    keyExtractor: (item: T, index: number) => string;
    maxItemWidth?: number;
    minItemWidth?: number;
    padding?: number;
    minColumns?: number;
    maxColumns?: number;
    gap?: number;
    style?: ViewStyle;
    isRefreshing?: boolean;
    onRefresh?: () => void;
    scrollEnabled?: boolean;
}

export function ResponsiveGrid<T>({
                                      data,
                                      renderItem,
                                      keyExtractor,
                                      maxItemWidth = 450,
                                      minItemWidth = 250,
                                      padding = 20,
                                      minColumns = 1,
                                      maxColumns = 10,
                                      gap = 12,
                                      style,
                                      isRefreshing,
                                      onRefresh,
                                      scrollEnabled
                                  }: ResponsiveGridProps<T>) {
    const {width} = useWindowDimensions();
    console.log('[ResponsiveGrid] Render with', {scrollEnabled});

    // Calculate number of columns based on screen width
    const numColumns = useMemo(() => {
        const availableWidth = width - padding;

        // Calculate columns ensuring items don't exceed max width
        let columns = Math.floor(availableWidth / minItemWidth);

        // it less item then colums, set columns to item length
        if (data.length < columns) {
            columns = data.length;
        }

        // Ensure item width doesn't exceed maxItemWidth
        const itemWidth = availableWidth / columns;
        if (itemWidth > maxItemWidth) {
            columns = Math.ceil(availableWidth / maxItemWidth);
        }

        console.log({
            maxColumns, minColumns, columns, maxItemWidth, minItemWidth
        });

        // Apply minimum columns constraint at the end
        return Math.min(maxColumns, Math.max(minColumns, columns));
    }, [width, padding, minItemWidth, maxItemWidth, minColumns]);

    // Calculate item width percentage based on columns
    const itemWidthPercentage = useMemo(() => {
        return `${100 / numColumns}%`;
    }, [numColumns]);
    console.log(itemWidthPercentage);

    const styles = useMemo(() => createStyles(maxItemWidth), [maxItemWidth]);

    const renderGridItem = useCallback(({item, index}: { item: T; index: number }) => {
        return (
            <View style={[styles.itemContainer, {width: itemWidthPercentage as DimensionValue}]}>
                {renderItem(item, index)}
            </View>
        );
    }, [renderItem, itemWidthPercentage, styles.itemContainer]);

    return (
        <FlatList
            data={data}
            renderItem={renderGridItem}
            keyExtractor={keyExtractor}
            numColumns={numColumns}
            key={numColumns}
            scrollEnabled={scrollEnabled}
            columnWrapperStyle={numColumns > 1 ? styles.columnWrapper : undefined}
            style={style}
            refreshControl={
                onRefresh && isRefreshing !== undefined ? (
                    <RefreshControl refreshing={isRefreshing} onRefresh={onRefresh}/>
                ) : undefined
            }
        />
    );
}

const createStyles = (maxWidth: number) => StyleSheet.create({
    columnWrapper: {
        justifyContent: 'flex-start'
    },
    itemContainer: {
        width: '100%',
        maxWidth: maxWidth
    },
});
